/* eslint-disable max-len, no-unused-vars */
class ScrollProgress {
  constructor( {
    barContainer = 'header',
    mode = 'inline',
    contentSelector = null,
    includeNextSibling = false,
    includeNextSiblingsCount = 0,
    extraSelectors = [],
    trackClass = 'e-div--progressbar',
    fillClass = 'progress-fill',
    activeClass = 'progress-bar-active',
    useCSSVar = true,
    waitForSectionMs = 0, // poll for section if missing
    waitIntervalMs = 100
  } = {} ) {
    this.container = document.querySelector( barContainer );
    if ( !this.container ) return;

    this.contentSelector = contentSelector;
    this.waitForSectionMs = Math.max( 0, waitForSectionMs );
    this.waitIntervalMs = Math.max( 16, waitIntervalMs );

    const normalizedCount = Number( includeNextSiblingsCount ) || ( includeNextSibling ? 1 : 0 );
    this.includeNextSiblingsCount = Math.max( 0, normalizedCount );

    if ( Array.isArray( extraSelectors ) ) {
      this.extraSelectors = extraSelectors;
    } else if ( typeof extraSelectors === 'string' && extraSelectors.trim() ) {
      this.extraSelectors = extraSelectors.split( ',' ).map( s => s.trim() );
    } else {
      this.extraSelectors = [];
    }

    this.trackClass = trackClass;
    this.fillClass = fillClass;
    this.activeClass = activeClass;
    this.useCSSVar = useCSSVar;

    this._frame = null;
    this._needsCalc = true;
    this._start = 0;
    this._end = 0;
    this._progress = 0;

    this._resolveSection().then( ( sectionFound ) => {
      if ( this.contentSelector && !sectionFound ) {
        // selector was requested but not found → abort (don’t fallback)
        return;
      }

      const { track, fill } = this._createDOM( mode );
      this.trackEl = track;
      this.fillEl = fill;

      this._onScroll = this._onScroll.bind( this );
      this._onResize = this._onResize.bind( this );

      this.refresh();
      window.addEventListener( 'scroll', this._onScroll, { passive: true } );
      window.addEventListener( 'resize', this._onResize );
    } );
  }

  /* Public */
  refresh() { this._calcSpan(); this._update(); }
  destroy() {
    cancelAnimationFrame( this._frame );
    window.removeEventListener( 'scroll', this._onScroll );
    window.removeEventListener( 'resize', this._onResize );
    this.trackEl?.parentNode?.removeChild( this.trackEl );
  }

  /* Resolve section */
  _resolveSection() {
    return new Promise( ( resolve ) => {
      const trySelect = () => {
        this.section = this.contentSelector ? document.querySelector( this.contentSelector ) : null;
        if ( this.section || !this.contentSelector ) {
          resolve( !!this.section );
          return true;
        }
        return false;
      };

      if ( trySelect() ) return;

      if ( this.waitForSectionMs <= 0 ) {
        resolve( false );
        return;
      }

      const start = performance.now();
      const tick = () => {
        if ( trySelect() ) return;
        if ( performance.now() - start >= this.waitForSectionMs ) {
          resolve( false );
          return;
        }
        setTimeout( tick, this.waitIntervalMs );
      };
      tick();
    } );
  }

  /* DOM */
  _createDOM( mode ) {
    const track = document.createElement( 'div' );
    const fill = document.createElement( 'div' );
    track.className = this.trackClass;
    fill.className = this.fillClass;

    if ( mode === 'fixed' ) {
      track.style.position = 'fixed';
      track.style.top = '0';
      track.style.left = '0';
      track.style.right = '0';
      track.style.zIndex = '9999';
      document.body.appendChild( track );
    } else {
      const cs = getComputedStyle( this.container );
      if ( cs.position === 'static' ) this.container.style.position = 'relative';
      this.container.appendChild( track );
    }

    track.appendChild( fill );
    return { track, fill };
  }

  /* Events */
  _onScroll() {
    if ( this._frame ) return;
    this._frame = requestAnimationFrame( () => { this._frame = null; this._update(); } );
  }
  _onResize() { this._needsCalc = true; this.refresh(); }

  /* Span */
  _sumNextSiblings( baseEl, count ) {
    let sum = 0, node = baseEl?.nextElementSibling || null, n = count;
    while ( node && n-- > 0 ) { sum += node.offsetHeight || 0; node = node.nextElementSibling; }
    return sum;
  }
  _sumExtraSelectors() {
    if ( !this.extraSelectors.length ) return 0;
    let sum = 0;
    this.extraSelectors.forEach( sel => {
      document.querySelectorAll( sel ).forEach( el => { sum += ( el.offsetHeight || 0 ); } );
    } );
    return sum;
  }

  _calcSpan() {
    const viewH = window.innerHeight;

    if ( !this.section ) {
      if ( this.contentSelector ) {
        // selector requested but still missing → no span
        this._start = 0; this._end = 0;
        this._needsCalc = false;
        return;
      }
      const doc = document.documentElement;
      const total = Math.max(
        doc.scrollHeight, doc.offsetHeight, doc.clientHeight,
        document.body?.scrollHeight || 0, document.body?.offsetHeight || 0
      );
      this._start = 0;
      this._end = Math.max( total - viewH, 0 );
      this._needsCalc = false;
      return;
    }

    const rect = this.section.getBoundingClientRect();
    const start = rect.top + window.scrollY;

    let H = this.section.offsetHeight
          + this._sumNextSiblings( this.section, this.includeNextSiblingsCount )
          + this._sumExtraSelectors();

    const end = Math.max( start + H - viewH, start );
    this._start = start;
    this._end = end;
    this._needsCalc = false;
  }

  /* Paint */
  _update() {
    if ( this._needsCalc ) this._calcSpan();

    const y = window.scrollY;
    const span = Math.max( this._end - this._start, 0 );
    let progress = 0;

    if ( span > 0 ) {
      progress = ( y - this._start ) / span;
    } else {
      progress = y > this._start ? 1 : 0;
    }

    this._progress = Math.min( 1, Math.max( 0, progress ) );
    const pct = ( this._progress * 100 ).toFixed( 3 ) + '%';

    this.fillEl.style.width = pct;
    if ( this.useCSSVar ) {
      document.documentElement.style.setProperty( '--scroll-progress', pct );
    }

    if ( this._progress > 0 ) {
      this.trackEl.classList.add( this.activeClass );
    } else {
      this.trackEl.classList.remove( this.activeClass );
    }
  }
}


const scrollProgress = new ScrollProgress( {
  barContainer: 'header',
  mode: 'inline',
  contentSelector: '.e-stn--rc-resource-inner-template-1-banner',
  includeNextSiblingsCount: 1,
  autoStyle: false,
  useCSSVar: false
} );
